import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';

import type { Fetcher } from './types/Fetcher';
import type { LoadingState } from './types/LoadingState';
import type { FetchStateDataSourceSpecification } from './types/FetchStateDataSourceSpecification.ts';
import type { ApiActionLogger } from './types/ApiActionLogger.ts';
import type { FetchStateCallback } from './types/FetchStateCallbacks.ts';
import type { FetchStateCacheConfig } from './types/CacheConfig.ts';
import type { FetchStateConfig } from './types/FetchStateConfig.ts';
import type { CacheRequestConfig, CacheProperties } from 'axios-cache-interceptor';
import type { FetchStateGlobalMiddleware } from './types/FetchStateGlobalMiddleware.ts';
import { HttpCancelledError } from '@/entities/httpClient/errors/HttpCancelledError.ts';
import { HttpTimeoutError } from '@/entities/httpClient/errors/HttpTimeoutError.ts';
import type { FetchConfig } from '@lib/mobx/fetch-state/types/FetchConfig.ts';
import type { DeepPartial } from '@lib/ts';

/**
 * @deprecated Legacy local FetchState. Superseded by the shared
 * `sn.ticketing.frontends.shared/default/mobx/fetch-state`, selected at runtime by the
 * `SharedFetchState` feature flag. Remove this class together with that flag.
 */
export class FetchState<TData = unknown, TError = unknown> implements LoadingState {
  private static _runningInstances: Set<FetchState> = new Set();
  private static _dataSources: FetchStateDataSourceSpecification<any>[] = [];
  private static _middlewares: Map<string, FetchStateGlobalMiddleware> = new Map();

  private callbacks?: FetchStateCallback<TData, NonNullable<typeof this.error>>;
  private cacheConfig?: FetchStateCacheConfig;
  private logger?: ApiActionLogger;
  private id?: string;
  private _latestFetchKey: string | null = null;
  private _isTimeoutCritical = true;

  protected ac = new AbortController();
  protected _activePromises: Set<Promise<any>> = new Set();

  isLoading = false;

  isError = false;

  isSuccess = false;

  /**
   * An optional field which will usually be controlled from an outside fetchState object.
   */
  isRefetching = false;

  error?: TError | null;

  data?: TData;

  isInitLoading = true;

  /**
   * Tracks whether a request were canceled or not
   */
  previouslyCancelled = false;

  static get middlewares() {
    return this._middlewares;
  }

  get latestFetchKey() {
    return this._latestFetchKey;
  }

  get activePromises() {
    return toJS(this._activePromises);
  }

  get isOngoing() {
    return Boolean(this.activePromises.size) || this.isLoading || this.isRefetching;
  }

  constructor(config?: FetchStateConfig) {
    this.logger = config?.logger;
    this.id = config?.id;
    this._isTimeoutCritical = config?.isTimeoutCritical ?? true;
    // todo: rework cache config. currently id is not working properly
    this.cacheConfig = config?.cache;

    makeObservable(this, {
      // @ts-expect-error should cover private field
      _activePromises: observable,
      _latestFetchKey: observable,
      //
      activePromises: computed,
      latestFetchKey: computed,
      isOngoing: computed,
      isLoading: observable,
      isError: observable,
      isSuccess: observable,
      isRefetching: observable,
      error: observable,
      data: observable,
      isInitLoading: observable,
      previouslyCancelled: observable,
      fetch: action,
      runInRefetchTransaction: action,
      abort: action,
      wipeData: action,
    });
  }

  /**
   * @description Used to create aggregated state from provided fetch states.
   * Methods are not being copied
   * @param args an array of `FetchState` instances
   */
  // types of fetch states are not required.
  // If passing unknown, it won't compile
  static combineLoadingState = (args: FetchState<any, any>[]) => {
    return args.reduce((acc, curr) => {
      return {
        isLoading: acc.isLoading || curr.isLoading,
        isError: acc.isError || curr.isError,
        isSuccess: acc.isSuccess || curr.isSuccess,
        isRefetching: acc.isRefetching || curr.isRefetching,
        isInitLoading: acc.isInitLoading && curr.isInitLoading,
      };
    }, {} as LoadingState);
  };

  /**
   * @description Kills all requests triggered by FetchState instance
   */
  static killAll = (args?: { keep?: string[] }) => {
    const { keep } = args || {};
    const idFiltersSet = new Set(keep || []);

    this._runningInstances.forEach((instance) => {
      if (!idFiltersSet.has(instance.getId()!)) {
        instance.abort();
      }
    });

    this._runningInstances.clear();
  };

  /**
   * @description Kills all active requests within a given scope (object).
   * May be used by FetchState instances something like that: FetchState.killScope(this), where this = any objects with some amount of FetchState instances
   *
   * @param scope
   */
  static killScope = (scope: object) => {
    Object.values(scope).forEach((value) => {
      if (value instanceof FetchState) {
        value.abort();
      }
    });
  };

  /**
   * @description Clear HTTP cache
   */
  static async clearCache(): Promise<void> {
    const { default: HttpClient } = await import('@/entities/httpClient');
    const storage = HttpClient.getCacheStorage();

    if (!storage) return;

    await storage.clear?.();
  }

  /**
   *
   * @param dataSourceSpecification
   */
  static configureDataSource<TResponse>(dataSourceSpecification: FetchStateDataSourceSpecification<TResponse>) {
    FetchState._dataSources.push(dataSourceSpecification);
  }

  static middleware(key: string, config: FetchStateGlobalMiddleware) {
    this._middlewares.set(key, config);

    return () => this._middlewares.delete(key);
  }

  /**
   *
   * @description fetch for nested response that follow BaseResponse pattern
   */
  async fetch(fetcher: Fetcher<TData>, config?: FetchConfig<TData, TError>): Promise<TData> {
    FetchState.addRunningInstance(this);

    // will never be empty by the end of fetch call
    let result: TData = {} as never;
    let scopedPromise: Promise<TData>;

    try {
      this.fetchStartHandler();

      const cacheRequestConfig = this.buildCacheRequestConfig();
      scopedPromise = fetcher(this.ac.signal, cacheRequestConfig || undefined);
      this._activePromises.add(scopedPromise);
      result = await scopedPromise;

      const dataSource = this.prepareDataSource(result);
      if (dataSource.isSuccess(result)) {
        this.successHandler(result, config);
        return result;
      }
      this.errorHandler(dataSource.error(result), dataSource.data(result), config);
    } catch (err) {
      // error pipe is enabled for catch
      this.errorHandler(err as NonNullable<typeof this.error>);

      return result;
    } finally {
      runInAction(() => this._activePromises.delete(scopedPromise));
      runInAction(() => (this.isInitLoading = false));
      runInAction(() => (this._latestFetchKey = crypto.randomUUID()));

      FetchState.removeRunningInstance(this);
    }

    return result;
  }

  setCallbacks(callbacks: typeof this.callbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Used to determine actions which are bringing whole ui rebuild (eg table filters change).
   * Such state may be accessed via `isRefetching` option
   */
  runInRefetchTransaction = action('Refetch action started', async (callback: Promise<unknown>) => {
    // runInAction should be used here in order to satisfy mobx strict mode
    runInAction(() => (this.isRefetching = true));
    await callback;
    runInAction(() => (this.isRefetching = false));
  });

  abort = action('Request abort', () => {
    this.ac.abort('Request aborted by FetchState');
    this.previouslyCancelled = true;
    this.ac = new AbortController();
  });

  wipeData() {
    this.isLoading = false;
    this.isError = false;
    this.isSuccess = false;
    this.callbacks = {};
    this.isInitLoading = false;
    this.error = undefined;
    this.data = undefined;
  }

  /**
   * WARNING! Manual control. Manually sets fetch state into the loading position. May be used for enhancing ui and provide better skeletons for end user.
   * Returns a function which may be called to halt the process (disposer). Disposer SHOULD BE called each time wait is called
   */
  wait = () => {
    const { promise: haltSignal, resolve } = Promise.withResolvers();

    runInAction(() => {
      this._activePromises.add(haltSignal);
      this.isLoading = true;
    });

    return () => {
      runInAction(() => {
        this.isLoading = false;
        resolve(null as never);
        this._activePromises.delete(haltSignal);
      });
    };
  };

  triggerSuccess = (args: { withData: TData }) => {
    const { withData } = args;

    this.successHandler(withData);

    return this;
  };

  /**
   * Iterates over each middleware and executes it in bottom-top order. Provides the isHandled flag to resolve whether you should handle this error twice.
   * @param args - an argument with which each middleware should be called
   */
  static executeMiddlewaresWith(args: { error?: unknown; success?: unknown }) {
    let isErrorHandled = false;
    let isSuccessHandled = false;

    const middlewaresArray = Array.from(this._middlewares.values());
    const bottomToTopMiddlewares = middlewaresArray.reverse();

    bottomToTopMiddlewares.forEach((m) => {
      if (args.error !== null && args.error !== undefined) {
        const { isHandled: isMiddlewareErrorHandled } = m.onError?.(args.error, args.success, isErrorHandled) || {};
        const isIgnoredReturnType = isMiddlewareErrorHandled === undefined;
        isErrorHandled = isIgnoredReturnType ? true : Boolean(isErrorHandled || isMiddlewareErrorHandled);
      }

      if (args.success !== null && args.success !== undefined) {
        const { isHandled: isMiddlewareSuccessHandled } = m.onSuccess?.(args.success, isSuccessHandled) || {};
        const isIgnoredReturnType = isMiddlewareSuccessHandled === undefined;
        isSuccessHandled = isIgnoredReturnType ? true : Boolean(isErrorHandled || isMiddlewareSuccessHandled);
      }
    });
  }

  /**
   * Build cache request config from FetchState cache config
   */
  protected buildCacheRequestConfig(): CacheRequestConfig | never {
    if (!this.cacheConfig?.enabled) {
      return { cache: false };
    }

    const cacheProperties: Partial<CacheProperties> = {};

    if (this.cacheConfig.ttl !== undefined) {
      cacheProperties.ttl = this.cacheConfig.ttl;
    }

    if (this.cacheConfig.cachePredicate) {
      cacheProperties.cachePredicate = {
        responseMatch: (response: any) => {
          return this.cacheConfig!.cachePredicate!(response.data);
        },
      };
    }

    const config: CacheRequestConfig = {
      cache: cacheProperties,
    };

    return config;
  }

  copy() {
    return new FetchState<TData, TError>({
      logger: this.logger,
      cache: this.cacheConfig,
    });
  }

  getId() {
    return this.id;
  }

  private prepareDataSource(response: TData) {
    const dataSource = FetchState._dataSources.find((x) => x.is(response));

    if (!dataSource)
      throw new Error(
        `Data source for such response doesn't specified: ${JSON.stringify(response, null, 2)}. Ensure to use one of the following formats (or specify another data source): ${JSON.stringify(FetchState._dataSources, null, 2)}`,
      );

    return dataSource;
  }

  private fetchStartHandler = () => {
    this.isLoading = true;
    this.isError = false;
    this.isSuccess = false;
    this.error = null;
  };

  private successHandler(data: TData, config?: FetchConfig<TData, TError>) {
    const isPipeIgnored = Boolean(config?.ignoreSuccessPipe?.(data as DeepPartial<TData>));

    runInAction(async () => {
      this.data = data;
      this.isSuccess = true;
      this.isLoading = false;
      this.previouslyCancelled = false;
      const callbackResult = await this.callbacks?.onSuccess?.(data);

      const customSuccessMessage = callbackResult?.loggerMessage;
      const defaultMessage = this.logger?.defaultMappers.success?.(data);

      !isPipeIgnored && FetchState.executeMiddlewaresWith({ success: data });
      this.logger?.success(customSuccessMessage || defaultMessage);
    }).catch((err) => {
      // The primary fetch succeeded; catch callback/middleware errors to prevent
      // unhandled promise rejections from the floating runInAction async task.
      this.logger?.error(err);
    });
  }

  private errorHandler(
    error: NonNullable<typeof this.error>,
    data: unknown = null,
    config?: FetchConfig<TData, TError>,
  ) {
    const isPipeIgnored = Boolean(config?.ignoreErrorPipe?.(error as DeepPartial<TError>));

    runInAction(() => {
      this.isError = true;
      this.isLoading = false;
      this.error = error;
      const callbackResult = this.callbacks?.onError?.(error);

      const customErrorMessage = callbackResult?.loggerMessage;
      const defaultMessage = this.logger?.defaultMappers.error?.(error);

      if (error instanceof HttpTimeoutError) {
        error.critical = this._isTimeoutCritical;
      }

      if (!(error instanceof HttpCancelledError)) {
        this.previouslyCancelled = false;
        !isPipeIgnored && FetchState.executeMiddlewaresWith({ error: error, success: data });
        this.logger?.error(customErrorMessage || defaultMessage);
      }
    });
  }

  private static removeRunningInstance(instance: FetchState<any, any>) {
    this._runningInstances.delete(instance);
  }

  private static addRunningInstance(instance: FetchState<any, any>) {
    this._runningInstances.add(instance);
  }
}
